// client/src/pages/Dashboard.jsx
import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import {Home,Users,UserCircle,Table,Calendar,MessageSquare,FileText,ClipboardList,ShoppingBag,Boxes,Megaphone,Globe,Menu,ChevronLeft,} from "lucide-react";
import logo from "../assets/logo.png"; // Ruta de tu logo
import { getRestaurantes } from "../api/restaurantes.js";
import DashboardInicio from "../components/DashboardInicio.jsx";
import RestaurantePage from "./RestaurantePage.jsx"
import UsuariosPage from "./UsuariosPage.jsx";
import ClientesPage from "./ClientesPage.jsx";
import ProductosPage from "./ProductosPage.jsx";
import MesasPage from "./MesasPage.jsx";
import ReservasPage from "./ReservasPage.jsx";
import PedidosPage from "./PedidosPage.jsx";
import FacturaPage from "./FacturaPage.jsx";
import InventarioPage from "./InventarioPage.jsx";  
import PublicacionesPage from "./PublicacionesPage.jsx";
import ReseniasPage from "./ReseniasPage.jsx";
import RedesSocialesPage from "./RedesSocialesPage.jsx";

export default function Dashboard() {
  const { usuario, cerrarSesion } = useAuth();
  const [moduloSeleccionado, setModuloSeleccionado] = useState("Inicio");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [perfilOpen, setPerfilOpen] = useState(false);
  const [restaurante, setRestaurante] = useState(null);
  const perfilRef = useRef(null);

  const handleLogout = () => {
    cerrarSesion();
    // Usar window.location para forzar redirección inmediata
    window.location.href = "/";
  };

  useEffect(() => {
    const fetchRestaurante = async () => {
      try {
        const data = await getRestaurantes();
        const restaurantes = data.restaurantes || [];
        
        // Si es SuperAdmin, mostrar mensaje genérico
        if (usuario?.isSuperAdmin) {
          setRestaurante({ 
            nombre_comercial: "Panel del Administrador", 
            razon_social: "Sistema Multi-Empresa" 
          });
        } else {
          // Si es usuario normal, buscar su restaurante por id_restaurante
          const miRestaurante = restaurantes.find(r => r.id_restaurante === usuario?.id_restaurante);
          if (miRestaurante) {
            setRestaurante(miRestaurante);
          }
        }
      } catch (error) {
        console.error("Error obteniendo restaurante:", error);
      }
    };
    
    if (usuario) {
      fetchRestaurante();
    }
  }, [usuario]);

  // Efecto para cerrar el menú de perfil al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (perfilRef.current && !perfilRef.current.contains(event.target)) {
        setPerfilOpen(false);
      }
    };

    if (perfilOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [perfilOpen]);

  const modulos = [
    { nombre: "Restaurante", icono: Home },
    { nombre: "Usuarios", icono: Users },
    { nombre: "Clientes", icono: UserCircle },
    { nombre: "Productos", icono: ShoppingBag },
    { nombre: "Mesas", icono: Table },
    { nombre: "Reservas", icono: Calendar },
    { nombre: "Pedidos", icono: ClipboardList },
    { nombre: "Ventas", icono: FileText },
    { nombre: "Inventarios", icono: Boxes },
    { nombre: "Publicaciones", icono: Megaphone },
    { nombre: "Reseñas", icono: MessageSquare },
    { nombre: "Redes Sociales", icono: Globe },
  ];

  return (
  <div className="flex min-h-screen bg-gray-100">
    {/* SIDEBAR IZQUIERDO */}
    <aside
      className={`bg-white shadow-md border-r border-gray-200 transition-all duration-300 flex flex-col ${
        sidebarCollapsed ? "w-20" : "w-64"
      }`}
    >
      {/* Logo y botón colapsar */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        {/* Logo del restaurante - clickeable para volver al inicio */}
        {!sidebarCollapsed && (
          <button 
            onClick={() => setModuloSeleccionado("Inicio")}
            className="focus:outline-none hover:opacity-80 transition"
          >
            <img 
              src={restaurante?.logo ? `${process.env.REACT_APP_API_URL || "http://localhost:4000"}${restaurante.logo}` : logo} 
              alt="Logo Restaurante" 
              className="h-24 w-auto object-contain cursor-pointer" 
            />
          </button>
        )}

        {/* Botón de colapsar siempre visible */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="p-2 rounded hover:bg-gray-100 transition"
        >
          {sidebarCollapsed ? <Menu size={20} /> : <ChevronLeft size={20} />}
        </button>
      </div>

      {/* Menú de módulos */}
      <nav className="flex-1 overflow-auto mt-4 pl-4 pr-[2px]">
        <ul className="flex flex-col gap-1">
          {modulos.map(({ nombre, icono: Icon }) => (
            <li key={nombre}>
              <button
                onClick={() => setModuloSeleccionado(nombre)}
                className={`flex items-center w-full p-3 rounded-lg transition ${
                  moduloSeleccionado === nombre
                    ? "bg-amber-100 text-amber-800 font-semibold"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                <Icon size={18} />
                {!sidebarCollapsed && <span className="ml-3">{nombre}</span>}
              </button>
            </li>
          ))}
        </ul>
      </nav>
    </aside>

      {/* CONTENIDO PRINCIPAL */}
      <div className="flex-1 flex flex-col">
        {/* Barra superior */}
        <header className="bg-amber-800 text-white py-5 px-6 flex justify-between items-center shadow-md">
          <div>
            <h1 className="text-xl font-bold">
              {restaurante?.nombre_comercial || "Cargando..."}
            </h1>
            {restaurante?.razon_social && restaurante.razon_social !== restaurante.nombre_comercial && (
              <p className="text-sm text-amber-100">
                {restaurante.razon_social}
              </p>
            )}
          </div>

          {/* Menú usuario */}
          <div className="relative" ref={perfilRef}>
            <button
              onClick={() => setPerfilOpen(!perfilOpen)}
              className="flex items-center gap-2 px-3 py-1 rounded hover:bg-amber-700 transition"
            >
              <UserCircle size={20} /> {/* Icono de usuario */}
              <span className="text-sm">
                {usuario?.nombre || usuario?.correo} ({usuario?.rol})
              </span>
            </button>

            {perfilOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white text-gray-800 shadow-lg rounded-md z-10">
                <button
                  className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                  onClick={() => {
                    setPerfilOpen(false);
                    alert("Mi perfil");
                  }}
                >
                  Mi perfil
                </button>
                <button
                  className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                  onClick={() => {
                    setPerfilOpen(false);
                    handleLogout();
                  }}
                >
                  Cerrar sesión
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Área de contenido */}
        <main className="flex-1 p-6 overflow-auto">
          {moduloSeleccionado === "Inicio" ? (
            <DashboardInicio />
          ) : moduloSeleccionado === "Restaurante" ? (
            <RestaurantePage />
          ) : moduloSeleccionado === "Usuarios" ? (
            <UsuariosPage />
          ) :moduloSeleccionado === "Clientes" ? (
            <ClientesPage />
          ) : moduloSeleccionado === "Productos" ? (
            <ProductosPage />
          ) :moduloSeleccionado === "Mesas" ? (
            <MesasPage />
          ) : moduloSeleccionado === "Reservas" ? (
            <ReservasPage />
          ) : moduloSeleccionado === "Pedidos" ? (
            <PedidosPage />
          ) : moduloSeleccionado === "Ventas" ? (
            <FacturaPage />
          ) : moduloSeleccionado === "Inventarios" ? (
            <InventarioPage />
          ) : moduloSeleccionado === "Publicaciones" ? (
            <PublicacionesPage />
          ) : moduloSeleccionado === "Reseñas" ? (
            <ReseniasPage />
          ) : moduloSeleccionado === "Redes Sociales" ? (
            <RedesSocialesPage />
          ) : (
            <DashboardInicio />
          )}
        </main>

      </div>
    </div>
  );
}
